import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import Layout from "@/components/Layout";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";

// Rotating sample questions grouped by user intent — refreshed on load and every ~20s.
const SAMPLE_POOL = [
  "How do I register as a Prime Supplier?",
  "How do I reserve my supplier station?",
  "What is included in the Supplier Package?",
  "Where can I upload my logo?",
  "How do I approve my artwork proof?",
  "Where can I view the event schedule?",
  "How do Business Matchmaking meetings work?",
  "Where can I find parking information?",
  "Where is my supplier station?",
  "What documents are available?",
  "How much does the Supplier Package cost?",
  "When does the summit begin?",
  "What is the dress code?",
  "How do I register as a Small Business Enterprise?",
  "How do I sign in to my dashboard?",
];

const SAMPLES_PER_VIEW = 4;
const pickSamples = () => {
  const shuffled = [...SAMPLE_POOL].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, SAMPLES_PER_VIEW);
};

// Strip stray markdown symbols so any residual ## or ** renders as clean prose.
const cleanMarkdown = (s = "") =>
  s
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/(?<!\*)\*(?!\*)(.+?)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^\s*[-•]\s+/gm, "• ");

export default function AskAI() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]);
  const [samples, setSamples] = useState(() => pickSamples());
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  // Rotate the visible sample set every 20 seconds while the user hasn't engaged yet.
  useEffect(() => {
    if (thread.length > 0) return;
    const id = setInterval(() => setSamples(pickSamples()), 20000);
    return () => clearInterval(id);
  }, [thread.length]);

  const ask = async (question) => {
    if (!question.trim() || busy) return;
    setBusy(true);
    setThread((t) => [...t, { role: "user", text: question }, { role: "assistant", text: "" }]);
    setQ("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const raw = part.slice(6);
          if (raw === "[DONE]") break;
          let chunk;
          try { chunk = JSON.parse(raw); } catch { chunk = raw; }
          setThread((t) => {
            const next = [...t];
            const last = next[next.length - 1];
            if (last?.role === "assistant") {
              const acc = last.text || "";
              if (chunk.startsWith(acc) && chunk.length > acc.length) {
                last.text = chunk;
              } else if (acc.endsWith(chunk)) {
                // duplicate — ignore
              } else {
                last.text = acc + chunk;
              }
            }
            return next;
          });
        }
      }
    } catch (e) {
      setThread((t) => {
        const next = [...t];
        const last = next[next.length - 1];
        if (last?.role === "assistant") {
          last.text = "I couldn't reach the concierge just now. Please try again, or email info@displayamerica.com.";
        }
        return next;
      });
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <section className="container-summit py-12 md:py-16 max-w-3xl">
        <div className="overline mb-4" data-testid="ai-overline"><span className="accent-rule" />Summit Concierge</div>

        {thread.length === 0 ? (
          <>
            <h1 className="text-4xl sm:text-5xl" data-testid="ai-welcome-title">Welcome to Summit Concierge.</h1>
            <p className="mt-5 text-zinc-600 max-w-2xl leading-relaxed" data-testid="ai-welcome-body">
              I'm here to help you navigate the Travel + Leisure Strategic Supplier Summit.
              Ask me about registration, supplier stations, event logistics, production status,
              Business Matchmaking, or the schedule — and I'll point you to the exact place on
              the platform to complete it.
            </p>

            <div className="mt-10">
              <div className="overline mb-4" data-testid="ai-try-asking"><span className="accent-rule" />Try asking</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {samples.map((s) => (
                  <button
                    key={s}
                    onClick={() => ask(s)}
                    data-testid={`ai-sample-${s.slice(0, 12).replace(/\s+/g, '-').toLowerCase()}`}
                    className="text-left p-4 border border-zinc-200 hover:border-black bg-white transition-colors text-sm group"
                  >
                    <Sparkles size={14} className="text-[#A39171] mb-2 group-hover:text-black transition-colors" />
                    <div>{s}</div>
                  </button>
                ))}
              </div>
              <p className="mt-4 text-xs text-zinc-400">Sample questions refresh automatically. Type your own below at any time.</p>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-4xl sm:text-5xl" data-testid="ai-active-title">Summit Concierge</h1>
            <p className="mt-4 text-sm text-zinc-500">Your executive event assistant.</p>
          </>
        )}

        <div className="mt-10 grid gap-6" data-testid="ai-thread">
          {thread.map((m, i) => (
            <div key={i} className={m.role === "user" ? "text-right" : ""}>
              <div className="overline mb-2">{m.role === "user" ? "You" : "Summit Concierge"}</div>
              <div className={`inline-block max-w-full text-left p-5 ${m.role === "user" ? "bg-black text-white" : "bg-white border border-zinc-200"} rounded-sm`}>
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                  {m.role === "assistant" ? (cleanMarkdown(m.text) || (busy ? "…" : "")) : m.text}
                </pre>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); ask(q); }} className="mt-8 sticky bottom-24 md:bottom-6 bg-white p-2 border border-black rounded-sm flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask the concierge…"
            data-testid="ai-input" className="h-12 rounded-none border-0 focus-visible:ring-0" />
          <button disabled={busy} className="btn-black h-12 px-5" data-testid="ai-send">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </form>
      </section>
    </Layout>
  );
}
